import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, RefreshCw, Search, X, ShoppingCart, Footprints, Loader2, Pin } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import WhatsAppChatPanel, { CsTemplateLite } from '../components/WhatsAppChatPanel';
import AiPauseControl from '../components/AiPauseControl';
import { apiFetch } from '../lib/api';

interface CrmConversation {
  phone: string;
  customer_name: string | null;
  last_message: string | null;
  last_message_at: number;
  last_activity_at: number | null;
  last_direction: string | null;
  unread_count: number;
  category: string;
  pinned: number;
  follow_up: string;
  note: string;
  ai_handoff: number;
}

interface Customer360 {
  phone: string;
  profile: { name?: string; email?: string; created_at?: number } | null;
  orderCount: number;
  totalSpent: number;
  summary: {
    orderCount: number; paidCount: number; totalSpent: number; avgOrder: number;
    firstOrderAt: number | null; lastOrderAt: number | null; pendingCount: number;
  };
  orders: { id: number; order_num: string; status: string; final_price: number; created_at: number;
    payment_confirmed: number; items: { name: string; quantity: number }[] }[];
  tags: { tag: string; color: string }[];
  wanla: { member_code: string; expire_at: number; next_push_at: number } | null;
  godfather: { id: number; status: string; task_status: string; name: string; campaign_name: string }[];
  state: { category: string; note: string; pinned: number; follow_up: string };
}

interface MessageHit {
  id: number;
  phone: string;
  customer_name: string;
  message: string;
  created_at: number;
}

const CATEGORIES = ['全部', '未分類', '售前查詢', '訂單跟進', '售後', '投訴', '其他'];

const fmtTime = (ts?: number | null) =>
  ts
    ? new Date(ts * 1000).toLocaleString('zh-HK', {
        ...(new Date(ts * 1000).toDateString() === new Date().toDateString()
          ? { hour: '2-digit', minute: '2-digit' }
          : { month: 'numeric', day: 'numeric' }),
      })
    : '';

const fmtPrice = (cents: number) => `HK$${(cents / 100).toFixed(0)}`;

const O_STATUS: Record<string, string> = {
  pending_whatsapp: '待客戶確認', pending_payment: '待付款', pending_confirmation: '待確認收款',
  payment_confirmed: '已收款·處理中', processing: '備貨中', shipped: '已發貨',
  completed: '已完成', cancelled: '已取消',
};
const EV_ICON: Record<string, string> = { web: '🌐', order: '📦', claim: '🎁', redeem: '✅' };

const AdminConversations: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<CrmConversation[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | 'human'>('all');
  const [category, setCategory] = useState('全部');
  const [search, setSearch] = useState('');
  const [msgHits, setMsgHits] = useState<MessageHit[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');
  const [customer360, setCustomer360] = useState<Customer360 | null>(null);
  const [c360Loading, setC360Loading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [subView, setSubView] = useState<null | 'orders' | 'journeys'>(null);
  const [journeyData, setJourneyData] = useState<{ bridged: boolean; timeline: { type: string; at: number; title: string; detail?: string }[] } | null>(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [showFullTrail, setShowFullTrail] = useState(false);
  const [csTemplates, setCsTemplates] = useState<CsTemplateLite[]>([]);
  const [listRefresh, setListRefresh] = useState(0);

  const fetchList = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter === 'unread') params.set('filter', 'unread');
      if (search.trim()) params.set('search', search.trim());
      if (category !== '全部') params.set('category', category);
      const res = await apiFetch(`/api/public/admin/crm/conversations?${params.toString()}`);
      if (res.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const json = await res.json();
      let rows: CrmConversation[] = (json.data ?? json).conversations || [];
      // 人工接手 filter（client-side）——ai_handoff=0＝AI 轉咗人／管理員接管，需要人跟
      if (filter === 'human') rows = rows.filter((r) => Number(r.ai_handoff) === 0);
      setConversations(rows);
    } finally {
      setLoading(false);
    }
  }, [filter, category, search, navigate]);

  useEffect(() => {
    const t = setTimeout(fetchList, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchList, listRefresh]);

  // 自動刷新：30 秘靜默輪詢收件箱（tab 隱藏時暫停）——新對話／未讀數自動浮現，唔使手動 F5
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) fetchList();
    }, 30_000);
    return () => clearInterval(id);
  }, [fetchList]);

  // 訊息內容搜尋（≥2 字，防抖）
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setMsgHits(null); return; }
    const t = setTimeout(async () => {
      const res = await apiFetch(`/api/public/admin/crm/search-messages?q=${encodeURIComponent(q)}`);
      if (res.ok) setMsgHits(((await res.json()).data ?? {}).hits || []);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // CS 話術（傳畀對話面板）
  useEffect(() => {
    apiFetch('/api/public/admin/cs-templates').then(async (res) => {
      if (res.ok) {
        const json = await res.json();
        setCsTemplates(((json.data ?? json).templates || []).filter((t: CsTemplateLite & { is_active: number }) => t.is_active));
      }
    }).catch(() => {});
  }, []);

  const openConversation = (conv: CrmConversation) => {
    setSelectedPhone(conv.phone);
    setSelectedName(conv.customer_name || conv.phone);
    setShowInfo(false);
    setSubView(null);
    // 打開即標已讀（fire-and-forget）
    apiFetch(`/api/public/admin/crm/state/${conv.phone}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ last_read_at: Math.floor(Date.now() / 1000) }),
    }).catch(() => {});
    // 客戶 360
    setC360Loading(true);
    setCustomer360(null);
    apiFetch(`/api/public/admin/crm/customer/${conv.phone}`).then(async (res) => {
      if (res.ok) setCustomer360(((await res.json()).data ?? {}));
    }).finally(() => setC360Loading(false));
    setTimeout(() => setListRefresh((n) => n + 1), 600);
  };

  const updateState = async (phone: string, fields: Record<string, unknown>) => {
    await apiFetch(`/api/public/admin/crm/state/${phone}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    setListRefresh((n) => n + 1);
  };

  const togglePin = async (conv: CrmConversation) => {
    const next = conv.pinned === 1 ? 0 : 1;
    setConversations((prev) => prev.map((c) => (c.phone === conv.phone ? { ...c, pinned: next } : c)));
    await updateState(conv.phone, { pinned: next });
  };

  const updateCategory = async (phone: string, cat: string) => {
    setCustomer360((prev) => (prev ? { ...prev, state: { ...prev.state, category: cat } } : prev));
    await updateState(phone, { category: cat });
  };

  const openJourneys = async () => {
    if (!customer360?.phone) return;
    setSubView('journeys');
    setJourneyLoading(true);
    setJourneyData(null);
    try {
      const res = await apiFetch(`/api/admin/journeys/timeline/${customer360.phone}`);
      if (res.ok) {
        const data = (await res.json()).data ?? {};
        setJourneyData({ bridged: !!data.bridged, timeline: (data.timeline || []).slice().reverse() });
      }
    } finally {
      setJourneyLoading(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) { localStorage.removeItem('admin_token'); navigate('/admin'); }
  };

  // ── 收件箱列表 ──
  const inbox = (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
        <div className="flex gap-1">
          {(['all', 'unread', 'human'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${filter === f ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'}`}
            >
              {f === 'all' ? '全部' : f === 'unread' ? '未讀' : '🙋 人工接手'}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜尋電話／姓名／訊息內容"
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-red-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </div>
          <button
            onClick={() => setListRefresh((n) => n + 1)}
            title="刷新收件箱"
            className="shrink-0 rounded-lg border border-gray-200 px-2.5 text-gray-500 transition hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-300 dark:hover:border-red-500"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        {/* TAG 分類篩選 */}
        <div className="flex gap-1 overflow-x-auto pb-0.5">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition ${category === cat ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <p className="p-8 text-center text-sm text-gray-400">載入中...</p>
        ) : conversations.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-400">{filter === 'unread' ? '冇未讀對話 🎉' : '暫無對話'}</p>
        ) : (
          conversations.map((c) => (
            <div
              key={c.phone}
              onClick={() => openConversation(c)}
              className={`group relative cursor-pointer border-b border-gray-100 px-3 py-2.5 transition hover:bg-red-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40 ${selectedPhone === c.phone ? 'bg-red-50 dark:bg-gray-700/60' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0">
                  {c.pinned === 1 && <Pin size={12} className="shrink-0 text-red-500 fill-red-500" />}
                  <span className="truncate text-sm font-bold text-gray-800 dark:text-gray-100">{c.customer_name || c.phone}</span>
                  {c.unread_count > 0 && <span className="shrink-0 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{c.unread_count}</span>}
                </span>
                {/* 📌 置頂掣（行內右側，唔阻點開對話） */}
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePin(c); }}
                    title={c.pinned === 1 ? '取消置頂' : '置頂'}
                    className={`p-1 rounded transition ${c.pinned === 1 ? 'text-red-500' : 'text-gray-300 hover:text-red-500 dark:text-gray-600'}`}
                  >
                    <Pin size={14} className={c.pinned === 1 ? 'fill-red-500' : ''} />
                  </button>
                  <span className="text-[11px] text-gray-400">{fmtTime(c.last_activity_at ?? c.last_message_at)}</span>
                </span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <span className="truncate text-xs text-gray-500 dark:text-gray-400">{c.last_message || '—'}</span>
                <span className="flex shrink-0 items-center gap-1">
                  {/* AI 模式標記：人工接手先顯示（預設 AI 接手係常態，唔好每行都標 🤖） */}
                  {Number(c.ai_handoff) === 0 && <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">🙋 人工</span>}
                  {c.category !== '未分類' && <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{c.category}</span>}
                </span>
              </div>
            </div>
          ))
        )}
        {msgHits && msgHits.length > 0 && (
          <div className="border-t-2 border-red-200 dark:border-red-800">
            <p className="bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 dark:bg-red-900/20">💬 訊息內容命中（{msgHits.length}）</p>
            {msgHits.map((h) => {
              const conv = conversations.find((c) => c.phone === h.phone);
              return (
                <button
                  key={h.id}
                  onClick={() => conv ? openConversation(conv) : openConversation({ phone: h.phone, customer_name: h.customer_name, last_message: h.message, last_message_at: h.created_at, last_activity_at: h.created_at, last_direction: null, unread_count: 0, category: '未分類', pinned: 0, follow_up: '', note: '', ai_handoff: 1 })}
                  className="block w-full border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40"
                >
                  <div className="flex justify-between gap-2">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{h.customer_name}</span>
                    <span className="text-[11px] text-gray-400">{fmtTime(h.created_at)}</span>
                  </div>
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">{h.message}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ── 客戶 360 面板內容 ──
  const customerPanel = (
    <div className="flex h-full flex-col overflow-y-auto min-h-0">
      {!selectedPhone ? (
        <p className="p-8 text-center text-sm text-gray-400">揀一個對話睇客戶資料</p>
      ) : c360Loading || !customer360 ? (
        <p className="p-8 text-center text-sm text-gray-400"><Loader2 size={18} className="mx-auto animate-spin" /></p>
      ) : subView ? (
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-center border-b border-gray-100 px-3 py-2.5 dark:border-gray-700">
            <button onClick={() => setSubView(null)} className="flex items-center gap-1 text-sm font-bold text-red-600">
              ← 返回客戶 360
            </button>
            <span className="ml-auto text-xs font-medium text-gray-500 dark:text-gray-400">{subView === 'orders' ? '訂單篩選' : '客戶蹤跡'}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {subView === 'orders' ? (
              <div className="space-y-3">
                {(customer360?.orders || []).length === 0 ? (
                  <p className="text-sm text-gray-400">暫無訂單</p>
                ) : customer360?.orders.map((o) => (
                  <div key={o.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <button onClick={() => window.open(`/admin/orders/${o.id}`, '_blank')} className="font-mono font-bold text-red-600 hover:underline">#{o.order_num}</button>
                      <span className="text-[11px] text-gray-400">{fmtTime(o.created_at)}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{O_STATUS[o.status] || o.status}</span>
                      <span className="font-bold text-gray-700 dark:text-gray-200">{fmtPrice(o.final_price)}</span>
                    </div>
                    {(o.items || []).length > 0 && (
                      <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300">
                        {o.items.map((it, i) => (
                          <div key={i} className="flex justify-between py-0.5">
                            <span className="truncate">{it.name}</span>
                            <span className="ml-2 shrink-0 text-gray-400">×{it.quantity}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {(customer360?.orders || []).length > 0 && (
                  <p className="text-[11px] text-gray-400">全部 {customer360?.orders.length} 張訂單（含明細） · 撳單號新分頁開詳情</p>
                )}
              </div>
            ) : journeyLoading ? (
              <p className="py-8 text-center text-gray-400"><Loader2 size={18} className="mx-auto animate-spin" /></p>
            ) : !journeyData ? (
              <p className="text-sm text-gray-400">—</p>
            ) : journeyData.timeline.length === 0 ? (
              <p className="text-sm text-gray-400">{journeyData.bridged ? '暫無旅程記錄' : '未有 web 訪客橋接（客人未瀏覽過網站，或未落單）'}</p>
            ) : (() => {
              // 洞見式：摘要統計＋里程碑（關鍵動作）＋可摺疊完整足跡
              const tl = journeyData.timeline;
              const web = tl.filter((e) => e.type === 'web');
              const pageViews = web.filter((e) => e.title === 'page_view');
              const actions = tl.filter((e) => !(e.type === 'web' && e.title === 'page_view'));
              const pageCounts: Record<string, number> = {};
              for (const e of pageViews) {
                const path = (e.detail || e.title).split(' · ')[0];
                pageCounts[path] = (pageCounts[path] || 0) + 1;
              }
              const topPages = Object.entries(pageCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);
              const actionNames: Record<string, string> = {
                add_to_cart: '加入購物車', checkout_start: '開始結帳', view_product: '睇產品',
                order: '落單', claim: '領取優惠', redeem: '兌換優惠',
              };
              const firstAt = tl.length ? tl[tl.length - 1].at : 0;
              const lastAt = tl.length ? tl[0].at : 0;
              const days = Math.max(1, Math.ceil((lastAt - firstAt) / 86400));
              return (
                <div className="space-y-4">
                  {/* 洞見摘要 */}
                  <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900">
                    <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                      活躍 {days} 日 · 瀏覽 {pageViews.length} 頁 · 關鍵動作 {actions.length} 次
                    </p>
                    {topPages.length > 0 && (
                      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        最常睇：{topPages.map(([p, n]) => `${p} ×${n}`).join(' · ')}
                      </p>
                    )}
                  </div>
                  {/* 里程碑（只列關鍵動作，最新在上） */}
                  {actions.length === 0 ? (
                    <p className="text-xs text-gray-400">未有關鍵動作（落單／領優惠等）——只有瀏覽記錄</p>
                  ) : (
                    <div className="relative space-y-4 border-l-2 border-red-200 pl-4 dark:border-red-900">
                      {actions.map((ev, i) => (
                        <div key={i} className="relative">
                          <span className="absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500 dark:border-gray-800" />
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            {EV_ICON[ev.type] || '•'} {actionNames[ev.title] || ev.title}
                          </p>
                          {ev.detail && <p className="text-xs text-gray-500 dark:text-gray-400">{ev.detail}</p>}
                          <p className="text-[11px] text-gray-400">{fmtTime(ev.at)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* 完整足跡（預設收起） */}
                  <details className="rounded-lg border border-gray-200 dark:border-gray-700">
                    <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                      🔍 完整足跡（{tl.length} 條）
                    </summary>
                    <div className="max-h-64 overflow-y-auto px-3 pb-2">
                      {tl.map((ev, i) => (
                        <div key={i} className="flex justify-between gap-2 border-b border-gray-100 py-1.5 text-xs last:border-0 dark:border-gray-700">
                          <span className="min-w-0 truncate text-gray-600 dark:text-gray-300">{EV_ICON[ev.type] || '•'} {ev.title}{ev.detail ? ` — ${ev.detail}` : ''}</span>
                          <span className="shrink-0 text-gray-400">{fmtTime(ev.at)}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="space-y-4 p-4">
          <div>
            <p className="text-lg font-bold text-gray-800 dark:text-white">{customer360.profile?.name || selectedName || customer360.phone}</p>
            <p className="text-xs text-gray-500">{customer360.phone}</p>
          </div>

          {/* 分類（AI 數據地基） */}
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">對話分類</p>
            <select
              value={customer360.state.category}
              onChange={(e) => updateCategory(customer360.phone, e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              {CATEGORIES.filter((c) => c !== '全部').map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="mt-1 text-[10px] text-gray-400">AI 掌櫃會自動標未分類對話；你手動改的類別不會被覆蓋。</p>
          </div>

          {/* 備註／跟進（AI 掌櫃與同事共用欄；失焦即存） */}
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">備註</p>
            <textarea
              defaultValue={customer360.state.note || ''}
              onBlur={(e) => { if (e.target.value !== (customer360.state.note || '')) updateState(customer360.phone, { note: e.target.value }); }}
              rows={3}
              placeholder="內部備註——AI 掌櫃與同事都看得到"
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">跟進事項</p>
            <input
              defaultValue={customer360.state.follow_up || ''}
              onBlur={(e) => { if (e.target.value !== (customer360.state.follow_up || '')) updateState(customer360.phone, { follow_up: e.target.value }); }}
              placeholder="例：等客人回退款截圖"
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* 標籤 */}
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">標籤</p>
            {customer360.tags.length === 0 ? <p className="text-xs text-gray-400">—</p> : (
              <div className="flex flex-wrap gap-1">
                {customer360.tags.map((t, i) => (
                  <span key={i} className="rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[10px] text-purple-700 dark:border-purple-800 dark:bg-purple-900/30 dark:text-purple-300">{t.tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* 客戶價值摘要 */}
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">客戶價值</p>
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900">
              {customer360.summary.orderCount === 0 ? (
                <div className="text-sm">
                  <p className="font-bold text-gray-700 dark:text-gray-200">未落單</p>
                  <p className="mt-0.5 text-xs text-gray-400">睇下對話有咩需要跟進——首次轉化最珍貴 🌶️</p>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline justify-between">
                    <p className="text-2xl font-bold text-gray-800 dark:text-white">{fmtPrice(customer360.summary.totalSpent)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">累計消費 · {customer360.summary.paidCount} 張已付款</p>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <span>平均 {fmtPrice(customer360.summary.avgOrder)}／單</span>
                    <span>共 {customer360.summary.orderCount} 張訂單</span>
                    <span>首單 {fmtTime(customer360.summary.firstOrderAt)}</span>
                    <span>最近 {fmtTime(customer360.summary.lastOrderAt)}</span>
                    {customer360.summary.pendingCount > 0 && (
                      <span className="col-span-2 font-medium text-amber-600">⏳ {customer360.summary.pendingCount} 張未付款——跟進機會</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 辣友卡 */}
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">辣友卡</p>
            {customer360.wanla ? (
              <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                🌶️ 會員 {customer360.wanla.member_code} · 到期 {new Date(customer360.wanla.expire_at * 1000).toLocaleDateString('zh-HK')}
              </div>
            ) : <p className="text-xs text-gray-400">非會員</p>}
          </div>

          {/* 谷友 */}
          <div>
            <p className="mb-1 text-xs font-bold text-gray-400 uppercase">谷友（推廣活動）</p>
            {customer360.godfather.length === 0 ? <p className="text-xs text-gray-400">—</p> : customer360.godfather.map((g) => (
              <div key={g.id} className="mb-1 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-300">
                {g.campaign_name || '活動'} ·{' '}
                {g.task_status === 'submitted' ? '✅ 已交分享' : g.status === 'waitlist' ? '後補' : g.status === 'sent' ? '已寄出' : '待寄出'}
              </div>
            ))}
          </div>

          {/* 快捷（抽屜內嵌子視圖——返回客戶360，唔跳走） */}
          <div className="flex gap-2">
            <button onClick={() => setSubView('orders')} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 px-2 py-2 text-xs text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-300">
              <ShoppingCart size={14} /> 訂單篩選
            </button>
            <button onClick={openJourneys} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 px-2 py-2 text-xs text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-300">
              <Footprints size={14} /> 客戶蹤跡
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── 對話區 ──
  const chatArea = selectedPhone ? (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gradient-to-r from-red-500 to-red-600 px-3 py-2.5 text-white dark:border-gray-700">
        <button onClick={() => { setSelectedPhone(null); setListRefresh((n) => n + 1); }} className="p-1 lg:hidden" aria-label="返回列表"><ArrowLeftIcon /></button>
        <MessageCircle size={18} className="hidden lg:block" />
        <span className="min-w-0 flex-1 truncate text-sm font-bold">{selectedName || selectedPhone}</span>
        <button
          onClick={() => setShowInfo(true)}
          className="xl:hidden rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white hover:bg-white/30 transition"
        >
          客戶360
        </button>
      </div>
      <WhatsAppChatPanel
        key={selectedPhone}
        phone={selectedPhone}
        heightClass="flex-1"
        csTemplates={csTemplates}
        onSent={() => setListRefresh((n) => n + 1)}
      />
    </div>
  ) : (
    <div className="flex h-full items-center justify-center text-gray-400">
      <div className="text-center">
        <MessageCircle size={40} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
        <p className="text-sm">揀左邊一個對話開始</p>
      </div>
    </div>
  );

  return (
    <AdminLayout currentPage="conversations" onLogout={handleLogout}>
      {/* B・急停控制（需求 B，2026-10-18）：浮動膠囊，急停＝AI 對全部客人靜音 30 分鐘 */}
      <AiPauseControl />
      {/* 桌面三欄鎖高 */}
      <div className="hidden lg:flex h-[calc(100vh-140px)] gap-4">
        <div className="w-[320px] shrink-0 rounded-xl border border-gray-200 bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-800">
          {inbox}
        </div>
        <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col dark:border-gray-700 dark:bg-gray-800">
          {chatArea}
        </div>
        {/* 客戶 360：只喺 ≥1280px 常駐；窄屏改用「客戶360」掣開抽屜（見下方） */}
        <div className="hidden xl:block w-[320px] shrink-0 rounded-xl border border-gray-200 bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-800">
          {customerPanel}
        </div>
      </div>

      {/* 手機：列表 / 全屏對話（覆蓋頁頭，最大化對話空間；底部 Tab 保留） */}
      <div className="lg:hidden">
        {selectedPhone ? (
          <div className="fixed inset-x-0 top-0 bottom-[60px] z-50 flex flex-col bg-gray-50 dark:bg-gray-900">
            {chatArea}
          </div>
        ) : (
          <div className="h-[calc(100dvh-165px)] rounded-xl border border-gray-200 bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-800">
            {inbox}
          </div>
        )}
      </div>

      {/* 客戶 360 抽屜（手機＋窄屏 desktop 1024–1280；≥1280 右欄常駐、掣已隱藏唔會觸發） */}
      {showInfo && selectedPhone && (
        <div className="fixed inset-0 z-[60] flex items-end bg-black/50" onClick={() => setShowInfo(false)}>
          <div className="max-h-[75vh] w-full rounded-t-2xl bg-white dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
              <span className="font-bold text-gray-800 dark:text-white">客戶 360</span>
              <button onClick={() => setShowInfo(false)} className="text-gray-400"><X size={20} /></button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto">{customerPanel}</div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>
);

export default AdminConversations;
